import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Trash2, Edit2, Image as ImageIcon, RefreshCw, X, Check, Instagram } from 'lucide-react';
import { getImageUrl } from '../../utils/getImageUrl';
import { CATEGORY_GROUPS, getCategoryLabel, normalizeCategory } from '../../data/categories';
import InstagramPrep from '../components/InstagramPrep';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const Products = () => {
  const { products, addProduct, updateProduct, deleteProduct, fetchProducts } = useAdminAuth();

  const [editingId, setEditingId] = useState(null);
  const [igProduct, setIgProduct] = useState(null);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('donlar');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const [sizesStock, setSizesStock] = useState({
    XS: 0,
    S: 0,
    M: 0,
    L: 0,
    XL: 0,
    XXL: 0,
  });

  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState('');
  const [backPreview, setBackPreview] = useState('');

  const [loading, setLoading] = useState(false);
  const [refreshed, setRefreshed] = useState(false);

  useEffect(() => {
    if (fetchProducts) {
      fetchProducts();
    }
  }, [fetchProducts]);

  const handleRefresh = async () => {
    setLoading(true);
    if (fetchProducts) {
      await fetchProducts();
    }
    setLoading(false);
    setRefreshed(true);
    setTimeout(() => {
      setRefreshed(false);
    }, 2000);
  };

  const handleImageChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSizeChange = (size, value) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setSizesStock((prev) => ({
      ...prev,
      [size]: isNaN(numValue) ? 0 : numValue,
    }));
  };

  const handleEditClick = (prod) => {
    setEditingId(prod.id);
    const prodName = typeof prod.name === 'object' ? (prod.name?.az || prod.name?.en || '') : prod.name || '';
    const prodDesc = typeof prod.description === 'object' ? (prod.description?.az || prod.description?.en || '') : prod.description || '';

    setName(prodName);
    setPrice(prod.price ? prod.price.toString() : '');
    setCategory(normalizeCategory(prod.category) || 'donlar');
    setDescription(prodDesc);

    if (prod.sizesStock) {
      try {
        const parsed = typeof prod.sizesStock === 'string' ? JSON.parse(prod.sizesStock) : prod.sizesStock;
        setSizesStock({
          XS: Number(parsed.XS ?? 0),
          S: Number(parsed.S ?? 0),
          M: Number(parsed.M ?? 0),
          L: Number(parsed.L ?? 0),
          XL: Number(parsed.XL ?? 0),
          XXL: Number(parsed.XXL ?? 0),
        });
      } catch {
        setSizesStock({ XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0 });
      }
    } else {
      const singleStock = Number(prod.stockQuantity ?? prod.stock ?? 0);
      setSizesStock({ XS: 0, S: 0, M: singleStock, L: 0, XL: 0, XXL: 0 });
    }

    setFrontFile(null);
    setBackFile(null);

    const imagesList = Array.from(prod.images || []);
    setFrontPreview(imagesList[0] ? getImageUrl(imagesList[0]) : '');
    setBackPreview(imagesList[1] ? getImageUrl(imagesList[1]) : '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName('');
    setPrice('');
    setCategory('donlar');
    setDescription('');
    setSizesStock({ XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0 });
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview('');
    setBackPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!name.trim()) {
      alert('Məhsul adını daxil edin!');
      return;
    }
    if (!price || isNaN(price) || parseFloat(price) <= 0) {
      alert('Düzgün qiymət daxil edin!');
      return;
    }

    const formattedSizesStock = {
      XS: Number(sizesStock.XS || 0),
      S: Number(sizesStock.S || 0),
      M: Number(sizesStock.M || 0),
      L: Number(sizesStock.L || 0),
      XL: Number(sizesStock.XL || 0),
      XXL: Number(sizesStock.XXL || 0),
    };

    const totalStock = Object.values(formattedSizesStock).reduce((acc, curr) => acc + curr, 0);

    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('price', price.toString());
    formData.append('stockQuantity', totalStock.toString());
    formData.append('sizesStock', JSON.stringify(formattedSizesStock));
    // Bazaya kateqoriyanın ID-si yazılır (məs. "donlar"), mətn yox.
    formData.append('category', category || 'donlar');
    formData.append('description', description ? description.trim() : '');
    formData.append('currency', '₼');
    formData.append('active', 'true');

    if (frontFile) formData.append('files', frontFile);
    if (backFile) formData.append('files', backFile);

    setSaving(true);
    try {
      if (editingId) {
        await updateProduct(editingId, formData);
        alert('Məhsul məlumatları uğurla yeniləndi!');
      } else {
        await addProduct(formData);
        alert('Məhsul uğurla əlavə olundu!');
      }

      handleCancelEdit();
      if (fetchProducts) await fetchProducts();
    } catch (err) {
      const errorData = err.response?.data;
      console.error('Xəta detalları:', errorData);

      if (errorData?.fields) {
        const messages = Object.entries(errorData.fields)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('\n');
        alert(`Validation Xətası:\n${messages}`);
      } else if (errorData?.error) {
        alert(errorData.error);
      } else {
        alert('Əməliyyat zamanı xəta baş verdi!');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu məhsulu silməyə əminsiniz?')) {
      try {
        if (deleteProduct) {
          await deleteProduct(id);
          if (fetchProducts) {
            await fetchProducts();
          }
        }
      } catch (err) {
        console.error("Məhsul silinərkən xəta:", err);
      }
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-serif text-gray-900 tracking-wide">Məhsul İdarəedilməsi</h2>
          <p className="text-sm text-gray-500 mt-1">Kataloqa yeni geyimlər əlavə et və razmerlər üzrə stok saylarına nəzarət et.</p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded shadow-sm text-sm font-medium transition-all duration-300 w-full sm:w-auto flex-shrink-0 ${
            refreshed
              ? 'bg-green-600 text-white border-green-600 scale-105'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {refreshed ? (
            <>
              <Check className="w-4 h-4 text-white animate-bounce" />
              Yeniləndi ✓
            </>
          ) : (
            <>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-600' : ''}`} />
              {loading ? 'Yenilənir...' : 'Stokları Yenilə'}
            </>
          )}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-1/3 bg-white p-5 rounded-md shadow-sm border border-gray-100 h-fit">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium font-serif flex items-center gap-2">
              {editingId ? (
                <>
                  <Edit2 className="w-4 h-4 text-blue-600" /> Məhsula Düzəliş Et
                </>
              ) : (
                '+ Yeni Məhsul Əlavə Et'
              )}
            </h3>
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-xs text-red-600 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" /> Ləğv Et
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Məhsulun Adı *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Məs: İpək Şifon Don"
                required
                className="w-full mt-1 border border-gray-200 rounded p-3 text-base sm:text-sm focus:outline-none focus:border-black"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Qiymət (₼) *</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  required
                  className="w-full mt-1 border border-gray-200 rounded p-3 text-base sm:text-sm focus:outline-none focus:border-black"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Kateqoriya</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full mt-1 border border-gray-200 rounded p-3 text-base sm:text-sm focus:outline-none focus:border-black bg-white"
                >
                  {CATEGORY_GROUPS.map((group) => (
                    <optgroup key={group.id} label={`── ${group.label.az} ──`}>
                      {group.items.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label.az}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                Razmerlər üzrə Stok Sayı
              </label>
              <div className="grid grid-cols-3 gap-2">
                {SIZES.map((sz) => (
                  <div key={sz} className="border border-gray-200 rounded p-1.5 bg-gray-50 flex flex-col justify-between">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase">{sz}</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={sizesStock[sz]}
                      onChange={(e) => handleSizeChange(sz, e.target.value)}
                      className="w-full mt-1 border border-gray-200 rounded p-2 text-sm text-center focus:outline-none focus:border-black bg-white"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Təsvir</label>
              <textarea
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Məhsul haqqında qısa məlumat..."
                className="w-full mt-1 border border-gray-200 rounded p-3 text-base sm:text-sm focus:outline-none focus:border-black resize-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Əsas Şəkil (Əvvəl)</label>
              <div className="mt-1 flex items-center gap-2 border border-dashed border-gray-200 rounded p-2">
                <ImageIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, setFrontFile, setFrontPreview)}
                  className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
              </div>
              {frontPreview && (
                <img src={frontPreview} alt="Preview" className="mt-2 w-16 h-20 object-cover rounded border" />
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">İkinci Şəkil (Arxa / Detal)</label>
              <div className="mt-1 flex items-center gap-2 border border-dashed border-gray-200 rounded p-2">
                <ImageIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, setBackFile, setBackPreview)}
                  className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
              </div>
              {backPreview && (
                <img src={backPreview} alt="Preview" className="mt-2 w-16 h-20 object-cover rounded border" />
              )}
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`mt-2 w-full text-white py-3.5 rounded text-sm font-medium transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
                editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black hover:bg-gray-800'
              }`}
            >
              {saving
                ? 'Yadda saxlanılır...'
                : editingId ? 'Dəyişikliyi Yadda Saxla' : 'Məhsulu Yadda Saxla'}
            </button>
          </form>
        </div>

        <div className="w-full lg:w-2/3 bg-white p-5 rounded-md shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium font-serif">Mövcud Məhsullar</h3>
            <span className="text-xs text-gray-400">Ümumi: {products ? products.length : 0} məhsul</span>
          </div>

          {/* ---------- MOBİL: kart görünüşü ---------- */}
          <div className="lg:hidden space-y-3">
            {(!products || products.length === 0) ? (
              <p className="text-center py-6 text-gray-400 text-sm">
                Hələ heç bir məhsul əlavə edilməyib.
              </p>
            ) : (
              products.map((prod) => {
                const prodName = typeof prod.name === 'object' ? (prod.name?.az || prod.name?.en) : prod.name;
                const imagesList = Array.from(prod.images || []);
                let parsedSizes = null;
                if (prod.sizesStock) {
                  try {
                    parsedSizes = typeof prod.sizesStock === 'string' ? JSON.parse(prod.sizesStock) : prod.sizesStock;
                  } catch {
                    parsedSizes = null;
                  }
                }
                const totalStock = Number(prod.stockQuantity ?? prod.stock ?? 0);

                return (
                  <div key={prod.id} className="border border-gray-100 rounded-xl p-3 bg-white">
                    <div className="flex gap-3">
                      <img
                        src={getImageUrl(imagesList[0] || prod.image)}
                        alt={prodName}
                        className="w-16 h-20 object-cover rounded bg-gray-100 border border-gray-200 flex-shrink-0"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                        }}
                      />

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{prodName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {prod.category ? getCategoryLabel(normalizeCategory(prod.category), 'az') : '-'}
                        </p>
                        <p className="font-semibold text-gray-900 mt-1">
                          {prod.price} {prod.currency || '₼'}
                        </p>
                      </div>

                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setIgProduct(prod)}
                          aria-label="Instagram üçün hazırla"
                          className="flex items-center justify-center w-10 h-10 text-pink-600 hover:bg-pink-50 rounded-lg transition-colors"
                        >
                          <Instagram className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditClick(prod)}
                          aria-label="Düzəliş et"
                          className="flex items-center justify-center w-10 h-10 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(prod.id)}
                          aria-label="Sil"
                          className="flex items-center justify-center w-10 h-10 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-50">
                      {parsedSizes ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(parsedSizes).map(([sz, val]) => {
                            const count = Number(val || 0);
                            return (
                              <span
                                key={sz}
                                className={`px-2 py-0.5 text-[11px] rounded font-medium ${
                                  count > 0 ? 'bg-gray-100 text-gray-700' : 'bg-red-50 text-red-400 line-through'
                                }`}
                              >
                                {sz}: {count}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded font-medium inline-block ${
                          totalStock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          Ümumi: {totalStock} ədəd
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ---------- DESKTOP: cədvəl ---------- */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold uppercase tracking-wider">
                  <th className="py-2 px-3">Şəkil</th>
                  <th className="py-2 px-3">Ad</th>
                  <th className="py-2 px-3">Kateqoriya</th>
                  <th className="py-2 px-3">Qiymət</th>
                  <th className="py-2 px-3">Razmer Stokları</th>
                  <th className="py-2 px-3 text-right">Əməliyyat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-sm">
                {(!products || products.length === 0) ? (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-400">
                      Hələ heç bir məhsul əlavə edilməyib.
                    </td>
                  </tr>
                ) : (
                  products.map((prod) => {
                    const prodName = typeof prod.name === 'object' ? (prod.name?.az || prod.name?.en) : prod.name;
                    const imagesList = Array.from(prod.images || []);

                    let parsedSizes = null;
                    if (prod.sizesStock) {
                      try {
                        parsedSizes = typeof prod.sizesStock === 'string' ? JSON.parse(prod.sizesStock) : prod.sizesStock;
                      } catch {
                        parsedSizes = null;
                      }
                    }

                    const totalStock = Number(prod.stockQuantity ?? prod.stock ?? 0);

                    return (
                      <tr key={prod.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-2 px-3">
                          <div className="flex gap-1.5">
                            {imagesList.length > 0 ? (
                              imagesList.slice(0, 2).map((img, idx) => (
                                <img
                                  key={idx}
                                  src={getImageUrl ? getImageUrl(img) : img}
                                  alt={`${prodName}-${idx}`}
                                  className="w-10 h-12 object-cover rounded bg-gray-100 border border-gray-200"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                                  }}
                                />
                              ))
                            ) : (
                              <img
                                src={getImageUrl ? getImageUrl(prod.image) : prod.image}
                                alt={prodName}
                                className="w-10 h-12 object-cover rounded bg-gray-100 border border-gray-200"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                                }}
                              />
                            )}
                          </div>
                        </td>

                        <td className="py-2 px-3 font-medium text-gray-900">{prodName}</td>
                        <td className="py-2 px-3 text-gray-500">
                          {prod.category ? getCategoryLabel(normalizeCategory(prod.category), 'az') : '-'}
                        </td>
                        <td className="py-2 px-3 font-semibold text-gray-900">{prod.price} {prod.currency || '₼'}</td>

                        <td className="py-2 px-3">
                          {parsedSizes ? (
                            <div className="flex flex-wrap gap-1 max-w-[180px]">
                              {Object.entries(parsedSizes).map(([sz, val]) => {
                                const count = Number(val || 0);
                                return (
                                  <span
                                    key={sz}
                                    className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                                      count > 0 ? 'bg-gray-100 text-gray-700' : 'bg-red-50 text-red-400 line-through'
                                    }`}
                                  >
                                    {sz}: {count}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className={`px-2 py-1 text-xs rounded font-medium inline-block ${
                              totalStock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              Ümumi: {totalStock} ədəd
                            </span>
                          )}
                        </td>

                        <td className="py-2 px-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setIgProduct(prod)}
                              className="text-pink-600 hover:text-pink-700 hover:bg-pink-50 p-1.5 rounded transition-colors cursor-pointer"
                              title="Instagram üçün hazırla"
                            >
                              <Instagram className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditClick(prod)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors cursor-pointer"
                              title="Düzəliş et"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(prod.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded transition-colors cursor-pointer"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <InstagramPrep
        product={igProduct}
        isOpen={!!igProduct}
        onClose={() => setIgProduct(null)}
      />
    </div>
  );
};

export default Products;